import { usePageHeader } from "contexts/PageHeaderContext";
import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  // CardDescription,
  // CardHeader,
  // CardTitle,
} from "../../ui/card";
import { Button } from "../../ui/button";
import { InputFieldset } from 'ui/inputFieldset';
import { SelectFieldset } from 'ui/selectFieldset';
import { SelectTriggerUI } from 'ui/selectTrigger';
import { Label } from "../../ui/label";
import { TextareaUI } from "ui/textareaUI";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "../../ui/select";
import { Checkbox } from "../../ui/checkbox";
import { UserPlus, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { getDetailsByITS } from "../../services/visaService";
import {
  getRoles,
  getVenues,
  requestUser,
  getMiqaats,
  RoleResponse,
  VenueResponse,
  MiqaatResposne,
  RequestUserPayload,
} from "../../services/userService";
import { 
  maskMobileNumber,
  maskEmail,
  maskWhatsAppNumber,
} from "../../utils/commonutils";
import { useScreenPermission } from "hooks/useScreenPermission";
import { FullScreenLoading } from "components/ui/loader";


export const RequestUserPage = () => {
  // Form state
  const [formItsNumber, setFormItsNumber] = useState("");
  const [formName, setFormName] = useState("");
  const [formEmail, setFormEmail] = useState("");
  const [formPhone, setFormPhone] = useState("");
  const [formWhatsAppNumber, setFormWhatsAppNumber] = useState("");
  const [formVenue, setFormVenue] = useState("");
  const [formEvent, setFormEvent] = useState("");
  const [formExpiryDate, setFormExpiryDate] = useState("");
  const [formRemark, setFormRemark] = useState("");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);

  const [rolesFromApi, setRolesFromApi] = useState<RoleResponse[]>([]);
  const [venues, setVenues] = useState<VenueResponse[]>([]);
  const [miqaats, setMiqaats] = useState<MiqaatResposne[]>([]);

  const [isFetchingIts, setIsFetchingIts] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isItsVerified, setIsItsVerified] = useState(false);
  // const [formIsNonItsUser, setFormIsNonItsUser] = useState(false);

  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [isEditingWhatsApp, setIsEditingWhatsApp] = useState(false);

  const [originalEmail, setOriginalEmail] = useState("");
  const [originalPhone, setOriginalPhone] = useState("");
  const [originalWhatsApp, setOriginalWhatsApp] = useState("");

  const { screenName } = useScreenPermission();
  const { setPageHeader } = usePageHeader();
  useEffect(() => {
    setPageHeader({
      title: screenName,
      childTitle: "Submit a user access request for approval",
      icon: <UserPlus className="h-5 w-5" />,
    });
    return () => setPageHeader(null);
  }, [setPageHeader, screenName]);


  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);

        const [venuesRes, rolesRes, miqaatsRes] = await Promise.all([
          getVenues(1, 100),
          getRoles(1, 100),
          getMiqaats(),
        ]);

        setVenues(venuesRes.items ?? []);

        const delegationRoles = (rolesRes.roles ?? []).filter(
          (r: RoleResponse) =>
            r.isActive && r.isAvailableForDelegation
        );

        setRolesFromApi(delegationRoles);
        setMiqaats(miqaatsRes.filter((m) => m.isActive));
      } catch (error) {
        console.error("Failed to load page data", error);
        toast.error("Failed to load page data");
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, []);

  const fetchItsDetails = async (itsNo: string) => {
    if (!itsNo || itsNo.length !== 8) {
      setIsItsVerified(false);
      return;
    }

    try {
      setIsFetchingIts(true);
      setLoading(true);

      const info = await getDetailsByITS(itsNo, true);

      if (!info) {
        // ITS not found
        setFormName("");
        setFormEmail("");
        setFormPhone("");
        setFormWhatsAppNumber("");
        // setFormIsNonItsUser(true);
        setIsItsVerified(false);

        toast.info("No record found. Marked as Non ITS User.");
        return;
      }

      // ITS found
      setFormName(info.fullName || "");
      setFormEmail(info.email || "");
      setOriginalEmail(info.email || "");

      setFormPhone(info.mobile_No || "");
      setOriginalPhone(info.mobile_No || "");

      setFormWhatsAppNumber(info.whatsApp_No || "");
      setOriginalWhatsApp(info.whatsApp_No || "");
      setIsItsVerified(true);
    } catch (error) {
      console.error(error);
      setFormName("");
      setFormEmail("");
      setFormPhone("");
      setFormWhatsAppNumber("");
      // setFormIsNonItsUser(true);
      setIsItsVerified(false);
    } finally {
      setIsFetchingIts(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formItsNumber.length === 8) {
      fetchItsDetails(formItsNumber);
    }

    if (formItsNumber.length !== 8) {
      setFormName("");
      setFormEmail("");
      setFormPhone("");
      setFormWhatsAppNumber("");
      setIsItsVerified(false);

      setIsEditingEmail(false);
      setIsEditingPhone(false);
      setIsEditingWhatsApp(false);
    }
  }, [formItsNumber]);

  const handleToggleRole = (roleId: string) => {
    setSelectedRoles((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId],
    );
  };



  const handleSubmitRequest = async () => {
    if (!formItsNumber) {
      toast.error("ITS Number is required");
      return;
    }

    if (formItsNumber.length !== 8) {
      toast.error("ITS Number must be 8 digits");
      return;
    }

    if (!formName || !formEmail) {
      toast.error("Name and Email are required");
      return;
    }

    if (!formVenue) {
      toast.error("Venue is required");
      return;
    }

    if (!formExpiryDate) {
      toast.error("Expiry Date is required");
      return;
    }

    if (!formEvent) {
      toast.error("Miqaat is required");
      return;
    }

    try {
      setLoading(true);
      const payload: RequestUserPayload = {
        userCode: `REQ-${Date.now()}`,
        itsNo: formItsNumber,
        fullName: formName.trim(),
        mobileNo: formPhone.trim(),
        whatsAppNumber: formWhatsAppNumber.trim(),
        email: formEmail.trim(),
        requestRemark: formRemark.trim(),
        validityStartDate: new Date().toISOString(),
        validityEndDate: new Date(formExpiryDate).toISOString(),
        venueId: Number(formVenue),
        miqaatId: Number(formEvent),
        roleIds: selectedRoles.map(Number),
      };

      await requestUser(payload);

      toast.success("User request submitted successfully");

      handleReset();
    } catch (error) {
      console.error(error);
      toast.error("Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormItsNumber("");
    setFormName("");
    setFormEmail("");
    setFormPhone("");
    setFormWhatsAppNumber("");
    setFormVenue("");
    setFormEvent("");
    setFormExpiryDate("");
    setFormRemark("");
    setSelectedRoles([]);
  };

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-6">
                {/* Form Card */}
        <Card
          className="bg-white/95 backdrop-blur-sm border-[#5B9BD5]/20 shadow-lg"
          style={{ paddingTop: "24px", marginTop: "20px" }}
        >
          {/* <CardHeader>
            <CardTitle style={{ color: "#5B9BD5" }}>User Request Form</CardTitle>
            <CardDescription className="text-gray-600">
              Fill in the details for the user access request. All fields marked
              with <span className="text-red-500">*</span> are required.
            </CardDescription>
          </CardHeader> */}
          <CardContent>
            <div className="space-y-6">
              {/* Basic Information Section */}
              <div className="space-y-4">
                <h3 className="text-sm text-gray-700 border-b border-gray-200 pb-2">
                  Basic Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <SelectFieldset label="Miqaat" required>
                      <Select value={formEvent} onValueChange={setFormEvent}>
                        <SelectTriggerUI className="border-gray-300">
                          <SelectValue placeholder="Select Miqaat" />
                        </SelectTriggerUI>
                        <SelectContent>
                          {miqaats.map((miqaat) => (
                            <SelectItem
                              key={miqaat.miqaatId}
                              value={miqaat.miqaatId.toString()}
                            >
                              {miqaat.miqaatName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </SelectFieldset>
                  </div>
                  <div className="space-y-2">
                    <InputFieldset
                      label="ITS Number"
                      required
                      value={formItsNumber}
                      onChange={(e) =>
                        setFormItsNumber(e.target.value.replace(/\D/g, ""))
                      }
                      placeholder="Enter 8-digit ITS number"
                      maxLength={8}
                    />

                    {isFetchingIts && (
                      <p className="text-xs text-[#5B9BD5]">
                        Fetching user details...
                      </p>
                    )}
                  </div>
                  {/* <div className="space-y-2">
                    <Label className="text-sm text-gray-700">User Type</Label>
                    <div className="flex items-center gap-3 h-10">
                      <Checkbox
                        checked={formIsNonItsUser}
                        onCheckedChange={(checked) =>
                          setFormIsNonItsUser(Boolean(checked))
                        }
                      />

                      <label
                        // htmlFor={`non-its-${applicant.id}`}
                        className="text-sm text-gray-700 cursor-pointer"
                      >
                        Non ITS User
                      </label>
                    </div>
                  </div> */}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <InputFieldset
                      label="Full Name"
                      required
                      value={formName}
                      disabled={isItsVerified}
                      onChange={(e) => setFormName(e.target.value)}
                      placeholder="Enter full name"
                      className="border-gray-300 focus:border-[#5B9BD5] focus:ring-[#5B9BD5]"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="relative">
                      <InputFieldset
                        label="Email Address"
                        required
                        type="email"
                        placeholder="user@fhims.com"
                        value={
                          isItsVerified && !isEditingEmail
                            ? maskEmail(originalEmail)
                            : formEmail
                        }
                        onKeyDown={(e) => {
                          if (isItsVerified && !isEditingEmail) {
                            setIsEditingEmail(true);
                            setFormEmail("");
                          }
                        }}
                        onChange={(e) => {
                          if (isEditingEmail) {
                            setFormEmail(e.target.value);
                          }
                        }}
                        className="border-gray-300 pr-16"
                      />

                      {isItsVerified && isEditingEmail && (
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingEmail(false);
                            setFormEmail(originalEmail);
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="relative">
                      <InputFieldset
                        label="Phone Number"
                        required
                        value={
                          isItsVerified && !isEditingPhone
                            ? maskMobileNumber(originalPhone)
                            : formPhone
                        }
                        placeholder="Enter phone number"
                        onKeyDown={() => {
                          if (isItsVerified && !isEditingPhone) {
                            setIsEditingPhone(true);
                            setFormPhone("");
                          }
                        }}
                        onChange={(e) => {
                          if (isEditingPhone) {
                            setFormPhone(e.target.value.replace(/\D/g, ""));
                          }
                        }}
                        className="border-gray-300 pr-16"
                      />

                      {isItsVerified && isEditingPhone && (
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingPhone(false);
                            setFormPhone(originalPhone);
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="relative">
                      <InputFieldset
                        label="WhatsApp Number"
                        value={
                          isItsVerified && !isEditingWhatsApp
                            ? maskWhatsAppNumber(originalWhatsApp)
                            : formWhatsAppNumber
                        }
                        placeholder="Enter whatsapp number"
                        onKeyDown={() => {
                          if (isItsVerified && !isEditingWhatsApp) {
                            setIsEditingWhatsApp(true);
                            setFormWhatsAppNumber("");
                          }
                        }}
                        onChange={(e) => {
                          if (isEditingWhatsApp) {
                            setFormWhatsAppNumber(
                              e.target.value.replace(/\D/g, ""),
                            );
                          }
                        }}
                        className="border-gray-300 pr-16"
                      />

                      {isItsVerified && isEditingWhatsApp && (
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingWhatsApp(false);
                            setFormWhatsAppNumber(originalWhatsApp);
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4">
                {/* Access Duration Section */}
                <div className="space-y-2">
                  <InputFieldset
                    label="Expiry Date"
                    required
                    type="date"
                    value={formExpiryDate}
                    onChange={(e) => setFormExpiryDate(e.target.value)}
                    className="border-gray-300 focus:border-[#5B9BD5] focus:ring-[#5B9BD5]"
                    min={new Date().toISOString().split("T")[0]}
                  />
                  <p className="text-xs text-gray-500">
                    Set the date when this user's access should expire
                  </p>
                </div>

                {/* Venue & Miqaat Section */}
                <div className="space-y-2">
                  <SelectFieldset label="Venue" required>
                    <Select value={formVenue} onValueChange={setFormVenue}>
                      <SelectTriggerUI className="border-gray-300">
                        <SelectValue placeholder="Select Venue" />
                      </SelectTriggerUI>
                      <SelectContent>
                        {venues
                          .filter((v) => v.isActive)
                          .map((venue) => (
                            <SelectItem
                              key={venue.venueId}
                              value={venue.venueId.toString()}
                            >
                              {venue.venueName}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </SelectFieldset>
                </div>
              </div>

              {/* Role Selection Section */}
              <div className="space-y-4">
                <h3 className="text-sm text-gray-700 border-b border-gray-200 pb-2 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" style={{ color: "#5B9BD5" }} />
                  Role Selection (Optional)
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {rolesFromApi.map((role) => (
                    <div
                      key={role.roleId}
                      className={`flex items-center space-x-3 p-3 rounded-lg border transition-all cursor-pointer ${
                        selectedRoles.includes(role.roleId.toString())
                          ? "border-[#5B9BD5] bg-[#5B9BD5]/5"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                      onClick={() => handleToggleRole(role.roleId.toString())}
                    >
                      <Checkbox
                        checked={selectedRoles.includes(role.roleId.toString())}
                        onCheckedChange={() =>
                          handleToggleRole(role.roleId.toString())
                        }
                      />
                      <Label className="text-sm">{role.roleName}</Label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500">
                  Select one or more roles that best match the user's
                  responsibilities. Leave empty if no appropriate role is
                  available.
                </p>
              </div>

              {/* Remark Section */}
              <div className="space-y-4">
                <h3 className="text-sm text-gray-700 border-b border-gray-200 pb-2">
                  Additional Instructions
                </h3>
                <div className="space-y-2">
                  <SelectFieldset label="Remark / Instructions (Optional)">
                    <TextareaUI
                      value={formRemark}
                      onChange={(e) => setFormRemark(e.target.value)}
                      placeholder="Add any special instructions or requirements for this user request..."
                      rows={4}
                      className="border-gray-300 focus:border-[#5B9BD5] focus:ring-[#5B9BD5] resize-none"
                    />
                  </SelectFieldset>
                  <p className="text-xs text-gray-500">
                    Provide details about role requirements, access level, or any
                    special permissions needed.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  className="flex-1 border-gray-300"
                >
                  Reset Form
                </Button>
                <Button
                  type="button"
                  onClick={handleSubmitRequest}
                  className="flex-1 text-white shadow-lg hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: "#5B9BD5" }}
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Submit Request
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
